<script setup>
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { getI18nKey } from 'dashboard/routes/dashboard/settings/helper/settingsHelper';

import Button from 'dashboard/components-next/button/Button.vue';
import { BaseTableRow, BaseTableCell } from 'dashboard/components-next/table';
import CustomRoleSourceChip from './CustomRoleSourceChip.vue';

const MAX_VISIBLE_SOURCES = 2;

defineProps({
  roles: {
    type: Array,
    required: true,
  },
  loading: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['edit', 'delete']);

const { t } = useI18n();

const inboxes = useMapGetter('inboxes/getInboxes');

const getRoleInboxes = role => {
  const ids = role.inbox_ids || [];
  return inboxes.value.filter(inbox => ids.includes(inbox.id));
};

const getHiddenSourcesTooltip = role =>
  getRoleInboxes(role)
    .slice(MAX_VISIBLE_SOURCES)
    .map(inbox => inbox.name)
    .join(', ');

const getFormattedPermissions = role => {
  return role.permissions
    .map(event => t(getI18nKey('CUSTOM_ROLE.PERMISSIONS', event)))
    .join(', ');
};
</script>

<template>
  <BaseTableRow
    v-for="customRole in roles"
    :key="customRole.id"
    :item="customRole"
  >
    <template #default>
      <BaseTableCell>
        <span class="text-body-main text-n-slate-12 truncate block">
          {{ customRole.name }}
        </span>
      </BaseTableCell>

      <BaseTableCell>
        <span class="text-body-main text-n-slate-11 line-clamp-2 block min-w-40 max-w-64">
          {{ customRole.description }}
        </span>
      </BaseTableCell>

      <BaseTableCell>
        <span class="text-body-main text-n-slate-11 block min-w-52 max-w-72">
          {{ getFormattedPermissions(customRole) }}
        </span>
      </BaseTableCell>

      <BaseTableCell>
        <span
          v-if="!getRoleInboxes(customRole).length"
          class="text-body-main text-n-slate-11"
        >
          {{ $t('CUSTOM_ROLE.LIST.SOURCES_ALL') }}
        </span>
        <div v-else class="flex flex-wrap items-center gap-1.5 min-w-56">
          <CustomRoleSourceChip
            v-for="inbox in getRoleInboxes(customRole).slice(
              0,
              MAX_VISIBLE_SOURCES
            )"
            :key="inbox.id"
            :inbox="inbox"
          />
          <span
            v-if="getRoleInboxes(customRole).length > MAX_VISIBLE_SOURCES"
            v-tooltip.top="getHiddenSourcesTooltip(customRole)"
            class="text-label-small text-n-slate-11"
          >
            {{
              $t('CUSTOM_ROLE.LIST.SOURCES_MORE', {
                n: getRoleInboxes(customRole).length - MAX_VISIBLE_SOURCES,
              })
            }}
          </span>
        </div>
      </BaseTableCell>

      <BaseTableCell align="end" class="w-24">
        <div class="flex gap-3 justify-end flex-shrink-0">
          <Button
            v-tooltip.top="$t('CUSTOM_ROLE.EDIT.BUTTON_TEXT')"
            icon="i-woot-edit-pen"
            slate
            sm
            @click="emit('edit', customRole)"
          />
          <Button
            v-tooltip.top="$t('CUSTOM_ROLE.DELETE.BUTTON_TEXT')"
            icon="i-woot-bin"
            slate
            sm
            class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
            :is-loading="loading[customRole.id]"
            @click="emit('delete', customRole)"
          />
        </div>
      </BaseTableCell>
    </template>
  </BaseTableRow>
</template>
